<template>
  <div class="onboarding_container">
    <progress-bar />

    <div class="step_component">
      <component
        :is="steps[currentStep]"
        v-bind="currentProps"
      />
    </div>

    <div
      v-if="currentStep"
      class="onboarding_nav"
    >
      <div class="d-flex flex-grow-1 flex-row justify-content-start">
        <secondary-button
          v-if="previousStep"
          :class="[
            processing || currentStep === 'COMPLETED'
              ? 'disabled'
              : '',
            'md',
            'back_button'
          ]"
          @click="updateCurrentStep(previousStep)"
        >
          Back
        </secondary-button>
      </div>

      <div class="d-flex flex-grow-1 flex-row justify-content-end">
        <default-button
          v-if="currentStep === 'SITEMAP'"
          fixed-width
          size="md"
          class="mr-4"
          button-style="secondary"
          @click="openKeywordReviewModal"
        >
          Review Sitemap
        </default-button>

        <VTooltip
          placement="top"
          theme="dark-blue-tooltip"
          :disabled="!disableContinue"
        >
          <div>
            <primary-button
              v-if="nextStep"
              :class="[
                processing ? 'disabled' : '',
                'md',
                'next_button'
              ]"
              :disabled="disableContinue"
              @click="nextStepFunction"
            >
              <i
                v-if="loading"
                class="fas fa-spinner fa-spin"
              />
              {{ nextStepText }}
            </primary-button>
          </div>
          <template #popper>
            You can’t continue until the snippet is installed on your website. Choose an installation option to proceed.
          </template>
        </VTooltip>
      </div>
    </div>
  </div>

  <review-sitemap-keywords-modal
    v-if="showKeywordReviewModal"
    @close="closeKeywordReviewModal"
  />
</template>

<script>
import ProgressBar from "./progress/ProgressBar";
import ConnectAccounts from "./steps/ConnectAccounts";
import KeywordIdeas from "./steps/KeywordIdeas";
import SitemapCreation from "./steps/SitemapCreation";
import PrimaryButton from "../global/buttons/PrimaryButton";
import SecondaryButton from "../global/buttons/SecondaryButton";
import {mapActions, mapGetters} from "vuex";
import DefaultButton from "../global/buttons/DefaultButton.vue";
import ReviewSitemapKeywordsModal from "../sitemap/modals/ReviewSitemapKeywordsModal.vue";
import Optimiser from "../onsiteEditor/components/installation/InstallationBox.vue";

export default {
  components: {
    ReviewSitemapKeywordsModal,
    DefaultButton,
    ProgressBar,
    PrimaryButton,
    SecondaryButton,
    SitemapCreation,
    KeywordIdeas,
    ConnectAccounts,
    Optimiser,
  },
  data() {
    return {
      components: {
        CONNECT_ACCOUNTS: 'ConnectAccounts',
        KEYWORD_IDEAS: 'KeywordIdeas',
        SITEMAP: 'SitemapCreation',
        OPTIMISER: 'Optimiser',
      },
      loading: false,
    }
  },
  computed: {
    ...mapGetters('onboarding', [
      'currentStep',
      'previousStep',
      'nextStep',
      'processing',
      'connectedAccounts',
    ]),
    ...mapGetters('keywordIdeas', [
      'chosenKeywords',
      'newChosenKeywords',
      'removeChosenKeywords'
    ]),
    ...mapGetters('strategy', [
      'showKeywordReviewModal',
    ]),
    ...mapGetters('user', [
      'user',
      'hasOptimizerAccessPermission',
      'activeWebsiteIsFreeAppsumo',
      'isAdmin',
    ]),
    ...mapGetters('onsiteEditorInstallation', [
        'optimiserInstalled',
        'canBypassOptimiser',
    ]),
    currentProps() {
      if(this.currentStep === "OPTIMISER") {
        return {
            isOnboarding: true,
        };
      }

      return {};
    },
    nextStepText() {
      if (
        this.currentStep === 'CONNECT_ACCOUNTS'
        && Object.values(this.connectedAccounts).every((account) => !account) // No connected accounts
      ) {
        return 'Skip Step';
      }

      if (this.currentStep === 'KEYWORD_IDEAS') {
        if (this.newChosenKeywords.length > 0 || this.removeChosenKeywords.length > 0) {
          return 'Save Keywords';
        }

        if (this.chosenKeywords.length === 0) {
          return 'Skip Step';
        }
      }

      if ((this.currentStep === 'SITEMAP' && (!this.hasOptimizerAccessPermission || this.activeWebsiteIsFreeAppsumo)) || this.currentStep === 'OPTIMISER') {
        return this.loading ? 'Finishing' : 'Finish Onboarding';
      }

      return 'Next Step';
    },
    steps() {
      let localComponents = { ...this.components };
      if (!this.hasOptimizerAccessPermission || this.activeWebsiteIsFreeAppsumo) {
          delete localComponents.OPTIMISER;
          return localComponents;
      }
      return this.components;
    },
    disableContinue() {
      if (this.isAdmin) {
        return false;
      }

      if (this.canBypassOptimiser) {
        return false;
      }

      return this.currentStep === 'OPTIMISER' && !this.optimiserInstalled;
    },
  },
  mounted() {
    this.fetchOnboardingData();
    this.getUser();
    this.fetchOptimiserInstallationStatus();
    this.fetchCanUserBypassOptimiserInstallation();
  },
  methods: {
    ...mapActions('onboarding', [
        'fetchOnboardingData',
        'updateCurrentStep',
        'completeOnboarding',
    ]),
    ...mapActions('keywordIdeas', [
      'openChosenKeywordsModal',
    ]),
    ...mapActions('strategy', [
      'openKeywordReviewModal',
      'closeKeywordReviewModal',
    ]),
    ...mapActions('user', [
        'getUser',
    ]),
    ...mapActions('onsiteEditorInstallation', [
      'fetchOptimiserInstallationStatus',
      'fetchCanUserBypassOptimiserInstallation',
    ]),
    async nextStepFunction() {
      if (
        this.currentStep === 'KEYWORD_IDEAS'
        && (this.newChosenKeywords.length > 0 || this.removeChosenKeywords.length > 0)
      ) {
        this.openChosenKeywordsModal();
        return;
      }

      if (this.nextStep === 'COMPLETED') {
        this.loading = true;
        await this.completeOnboarding();
        window.mixpanel('Onboarding: Complete Onboarding', {
          website_id: this.user?.active_website?.id,
        })
        this.loading = false;
        return;
      }

      this.updateCurrentStep(this.nextStep);
    }
  },
}
</script>

<style lang="scss" scoped>
.onboarding_container {
  padding: 0 15px;
  width: 100vw;

  .onboarding_nav {
    display: flex;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 1rem;
    background: white;
  }
}
</style>
